import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";

import * as AuthServices from '../services/auth.service';
import * as UserServices from '../../user/services/user.service';
import * as AdvertiserUserServices from '../services/user.service';
import { ATLEAST_ONE_CAPITAL_REGEX, ATLEAST_ONE_NUMBER_REGEX, ATLEAST_ONE_SMALL_REGEX, ATLEAST_ONE_SPECIAL_CHARACTER_REGEX, PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH, ROLE, SWAL_SETTINGS } from "../../../utils/Constants";
import { PHONE_VALIDATION } from "../../../utils/CommonValidatiobn";
import { handleServerValidations, setLocalKey, getUser } from "../../../utils/commonfunction";
import PhoneInput from "../../../utils/PhoneInput";
import { addProfileData } from "../../../redux/slices/profileslice";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

const getCleanDigits = (val) => String(val || "").replace(/\D/g, "").slice(-10);

function KycDetail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const Dispatch = useDispatch();
  const { t } = useTranslation();

  const locationRef = useRef({ latitude: null, longitude: null });
  const kycDocFrontRef = useRef(null);
  const kycDocBackRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Detect user (become-advertiser) flow vs regular advertiser flow
  const [currentUserData, setCurrentUserData] = useState(() => getUser(ROLE.USER) || getUser(ROLE.ADVERTISER) || {});
  const userDataForUser = currentUserData;
  const isUserFlow = userDataForUser && userDataForUser.token && userDataForUser.role === ROLE.USER;

  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState(() => {
    const initialUser = getUser(ROLE.USER) || getUser(ROLE.ADVERTISER) || {};
    if (initialUser?.is_kyc_phone_verified) {
      return getCleanDigits(initialUser.kyc_mobile_number);
    }
    return "";
  });

  useEffect(() => {
    const fetchFreshDetails = async () => {
      try {
        let res;
        if (isUserFlow) {
          res = await UserServices.profileDetails({ _id: currentUserData?._id });
        } else {
          res = await AdvertiserUserServices.profileDetails();
        }
        if (res && res.data) {
          setCurrentUserData((prev) => ({ ...prev, ...res.data }));
          if (res.data.is_kyc_phone_verified) {
            setVerifiedPhoneNumber(getCleanDigits(res.data.kyc_mobile_number));
          }
        }
      } catch (err) {
        console.log("Failed to fetch fresh KYC details:", err);
      }
    };
    fetchFreshDetails();
  }, []);

  const validationSchema = Yup.object().shape({
    kyc_name: Yup.string()
      .required(t("vaildation.kyc_name")),
    kyc_dob: Yup.string()
      .required(t("vaildation.kyc_dob"))
      .test("not-in-future",  "DOB cannot be in the future", function (value) {
        if (!value) return false;

        const selectedDate = new Date(value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        return selectedDate <= today;
      })
      .test("age",  "You must be at least 18 years old", function (value) {
        if (!value) return false;

        const today = new Date();
        const birthDate = new Date(value);

        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (
          monthDiff < 0 ||
          (monthDiff === 0 && today.getDate() < birthDate.getDate())
        ) {
          age--;
        }

        return age >= 18;
      }),
    kyc_gender: Yup.string().required(t("vaildation.kyc_gender")),
    kyc_mobile_number: Yup.string()
      .required(t("vaildation.kyc_mobile_number_required"))
      .matches(/^\d{10}$/, t("vaildation.kyc_mobile_number_invalid")),
    kyc_doc_type: Yup.string().required(t("vaildation.kyc_doc_type")),
    kyc_doc_number: Yup.string()
      .required(t("vaildation.kyc_doc_number"))
      .test("valid-doc", t("Invalid document number"), function (value) {
        const { kyc_doc_type } = this.parent;
        if (!value) return false;
        if (kyc_doc_type === "aadhar") {
          return /^\d{12}$/.test(value);
        } else if (kyc_doc_type === "pan") {
          return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value.toUpperCase());
        } else if (kyc_doc_type === "passport") {
          return /^[A-Z]{1}[0-9]{7}$/.test(value.toUpperCase());
        }
        return true;
      }),
    kyc_social_media: Yup.array().optional()
      .of(
        Yup.string()
          .trim()
          .url(t("vaildation.kyc_social_media.url"))
          .max(500, t("vaildation.kyc_social_media.max_too_long_error"))
      )
      .max(10, t("vaildation.kyc_social_media.max_length_allow")),
    kyc_doc_front: Yup.mixed()
      .test("required-doc-front", t("vaildation.kyc_doc_front"), function (value) {
        if (userDataForUser?.kyc_doc_front) return true;
        return !!value;
      }),
    kyc_doc_back: Yup.mixed()
      .test("required-doc-back", t("vaildation.kyc_doc_back"), function (value) {
        if (userDataForUser?.kyc_doc_back) return true;
        return !!value;
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      kyc_name: userDataForUser?.kyc_name || userDataForUser?.name || '',
      kyc_dob: userDataForUser?.kyc_dob ? (typeof userDataForUser.kyc_dob === 'string' ? userDataForUser.kyc_dob.split("T")[0] : '') : '',
      kyc_gender: userDataForUser?.kyc_gender || '',
      kyc_mobile_number: userDataForUser?.kyc_mobile_number
        ? String(userDataForUser.kyc_mobile_number).replace(/^\+91\s*/, "")
        : (userDataForUser?.phone_number ? String(userDataForUser.phone_number).replace(/^\+91\s*/, "") : ''),
      kyc_doc_type: userDataForUser?.kyc_doc_type || 'aadhar',
      kyc_doc_number: userDataForUser?.kyc_doc_number || '',
      kyc_social_media: Array.isArray(userDataForUser?.kyc_social_media) && userDataForUser.kyc_social_media.length > 0
        ? userDataForUser.kyc_social_media
        : [''],
      kyc_doc_front: userDataForUser?.kyc_doc_front || '',
      kyc_doc_back: userDataForUser?.kyc_doc_back || ''
    },
    validationSchema: validationSchema,
    validateOnBlur: false,
    onSubmit: (values) => {
      // Verification check bypassed for now - proceed even if unverified
      /*
      const cleanPhone = getCleanDigits(values.kyc_mobile_number);
      const isVerified = Boolean(
        cleanPhone &&
        cleanPhone.length === 10 &&
        verifiedPhoneNumber &&
        cleanPhone === verifiedPhoneNumber
      );

      if (!isVerified) {
        // Do not submit until phone number is verified, and auto call verify!
        handleSendOtp(values.kyc_mobile_number, true);
        return;
      }
      */

      submitKycForm(values);
    },
  });

  const submitKycForm = (values) => {
    setSubmitted(true);
    let formData = new FormData();
    formData.append("kyc_name", values.kyc_name);
    formData.append("kyc_dob", values.kyc_dob);
    formData.append("kyc_gender", values.kyc_gender);
    formData.append(
      "kyc_mobile_number",
      `+91 ${values.kyc_mobile_number}`
    );
    formData.append("kyc_doc_type", values.kyc_doc_type);
    formData.append("kyc_doc_number", values.kyc_doc_number);
    formData.append("kyc_social_media", JSON.stringify(values.kyc_social_media));
    if (values.kyc_doc_front instanceof File) {
      formData.append("kyc_doc_front", values.kyc_doc_front);
    }
    if (values.kyc_doc_back instanceof File) {
      formData.append("kyc_doc_back", values.kyc_doc_back);
    }

    AuthServices
      .updateKYC(formData)
      .then((response) => {
        setSubmitted(false);

        if (response.success) {
          if (isUserFlow) {
            // User become-advertiser flow: save to user localStorage
            const existingUserData = getUser(ROLE.USER);
            const token = existingUserData?.token || response?.data?.token;
            const updatedUserData = {
              ...existingUserData,
              ...response.data,
              token,
              isKycRegistered: true,
              is_phone_verified: true,
              failed_verification_reason: null,
            };
            setLocalKey(ROLE.USER, JSON.stringify(updatedUserData));
            Dispatch(addProfileData(updatedUserData));
          } else {
            const existingAdvertiserData = getUser(ROLE.ADVERTISER);
            const token = existingAdvertiserData?.token || response?.data?.token;
            const updatedAdvertiserData = {
              ...existingAdvertiserData,
              ...response.data,
              token,
              isKycRegistered: true,
              is_phone_verified: true,
              failed_verification_reason: null,
            };
            setLocalKey(ROLE.ADVERTISER, JSON.stringify(updatedAdvertiserData));
            Dispatch(addProfileData(updatedAdvertiserData));
          }

          Swal.fire({
            icon: "success",
            title: "KYC Submitted!",
            text: response.message || "Thank you! Your KYC details have been submitted successfully for verification.",
            confirmButtonText: "OK",
            confirmButtonColor: "#f43f5e",
          }).then(() => {
            navigate("/advertiser/success-submitted");
          });
        } else {
          Swal.fire({
            icon: "error",
            text: handleServerValidations(response),
            ...SWAL_SETTINGS,
          });
        }
      })
      .catch((error) => {
        setSubmitted(false);
        console.log("error =====> ", error);
        Swal.fire({
          icon: "error",
          text: error?.message || "Something went wrong while submitting KYC.",
          ...SWAL_SETTINGS,
        });
      });
  };

  const handleSendOtp = async (phoneNumber, autoSubmitAfterVerify = false) => {
    const cleanPhone = getCleanDigits(phoneNumber);
    if (!cleanPhone || cleanPhone.length !== 10) {
      formik.setFieldTouched("kyc_mobile_number", true);
      formik.setFieldError("kyc_mobile_number", t("vaildation.kyc_mobile_number_invalid"));
      Swal.fire({
        icon: "error",
        text: "Please enter a valid 10-digit mobile number.",
        ...SWAL_SETTINGS,
      });
      return;
    }

    setIsVerifyingOtp(true);
    try {
      const payload = {
        _id: currentUserData._id,
        phone_number: cleanPhone,
        type: "kyc_phone",
      };

      const response = isUserFlow
        ? await UserServices.sendUpdateOtp(payload)
        : await AdvertiserUserServices.sendUpdateOtp(payload);

      if (response && response.success) {
        setOtpSent(true);
        const { value: otp } = await Swal.fire({
          title: "Verify Phone Number",
          text: `Enter the 6-digit OTP sent to +91 ${cleanPhone}`,
          input: "text",
          inputLabel: "Enter 6-digit OTP",
          inputPlaceholder: "123456",
          showCancelButton: true,
          confirmButtonText: "Verify",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#f43f5e",
          inputAttributes: {
            maxlength: 6,
            autocapitalize: "off",
            autocorrect: "off",
          },
          inputValidator: (value) => {
            if (!value || value.length !== 6 || !/^\d+$/.test(value)) {
              return "Please enter a valid 6-digit OTP";
            }
          },
        });

        if (otp) {
          await handleVerifyOtp(cleanPhone, otp, autoSubmitAfterVerify);
        } else {
          setOtpSent(false);
        }
      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response) || "Failed to send OTP",
          ...SWAL_SETTINGS,
        });
        setOtpSent(false);
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        text: "Something went wrong while sending OTP.",
        ...SWAL_SETTINGS,
      });
      setOtpSent(false);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleVerifyOtp = async (phoneNumber, otp, autoSubmitAfterVerify = false) => {
    try {
      const payload = {
        _id: currentUserData._id,
        phoneOtp: otp,
        type: "kyc_phone",
      };

      const response = isUserFlow
        ? await UserServices.verifyUpdateOtp(payload)
        : await AdvertiserUserServices.verifyUpdateOtp(payload);

      if (response && response.success) {
        setVerifiedPhoneNumber(phoneNumber);
        const updatedUserData = {
          ...currentUserData,
          kyc_mobile_number: `+91 ${phoneNumber}`,
          is_kyc_phone_verified: true,
        };
        setCurrentUserData(updatedUserData);
        if (isUserFlow) {
          setLocalKey(ROLE.USER, JSON.stringify(updatedUserData));
        } else {
          setLocalKey(ROLE.ADVERTISER, JSON.stringify(updatedUserData));
        }
        Dispatch(addProfileData(updatedUserData));

        await Swal.fire({
          icon: "success",
          text: "Phone number verified successfully!",
          ...SWAL_SETTINGS,
        });

        setOtpSent(false);

        if (autoSubmitAfterVerify) {
          submitKycForm({
            ...formik.values,
            kyc_mobile_number: phoneNumber,
          });
        }
      } else {
        Swal.fire({
          icon: "error",
          text: handleServerValidations(response) || "Invalid OTP",
          ...SWAL_SETTINGS,
        });
      }
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        text: "Something went wrong during verification.",
        ...SWAL_SETTINGS,
      });
    }
  };

  console.log(formik.errors);

  const addLink = (e) => {
    e.preventDefault();
    const arr = [...(formik.values.kyc_social_media || [])];
    arr.push("");
    formik.setFieldValue("kyc_social_media", arr);
  };

  const removeLink = (e, index) => {
    e.preventDefault();
    const arr = [...(formik.values.kyc_social_media || [])];
    arr.splice(index, 1);
    formik.setFieldValue("kyc_social_media", arr);
  };

  const currentInputPhone = getCleanDigits(formik.values.kyc_mobile_number);
  const isPhoneVerified = Boolean(
    currentInputPhone &&
    currentInputPhone.length === 10 &&
    verifiedPhoneNumber &&
    currentInputPhone === verifiedPhoneNumber
  );

  return (
    <section className="py-8 md:py-12 lg:py-16 form-bg">
      <div className="container">
        <h1 className="headings-h1 text-center text-zinc-950 mb-8">
          {t("advertiser.kyc_details.heading")}
        </h1>

        <div className="bg-white p-[15px] md:p-[50px] rounded-[10px] max-w-[650px] shadow-[0px_2px_24px_rgba(0,0,0,0.05)] w-full mx-auto border border-zinc-100">
          {userDataForUser?.failed_verification_reason && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r">
              <div className="flex items-start">
                <div className="ml-1">
                  <h3 className="text-sm font-bold text-red-800">
                    Previous Verification Request Rejected
                  </h3>
                  <div className="mt-1 text-sm text-red-700">
                    <p><strong>Reason:</strong> {userDataForUser.failed_verification_reason}</p>
                  </div>
                  <p className="mt-1 text-xs text-red-600">Please review and update your KYC details below to re-submit.</p>
                </div>
              </div>
            </div>
          )}
          <form onSubmit={formik.handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-[30px]">
              <div className="form-group">
                <label htmlFor="kyc_name">{t("name")}</label>
                <input
                  id="kyc_name"
                  type="text"
                  placeholder={t("placeholder.kyc_name")}
                  className="form-control-custom"
                  // required
                  name="kyc_name"
                  value={formik.values.kyc_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.kyc_name && formik.touched.kyc_name && formik.errors.kyc_name}
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="kyc_dob">{t("date_of_birth")}</label>
                <input
                  id="kyc_dob"
                  type="date"
                  name="kyc_dob"
                  max={new Date().toISOString().split("T")[0]}
                  className="form-control-custom"
                  value={formik.values.kyc_dob}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.kyc_dob && formik.touched.kyc_dob && formik.errors.kyc_dob}
                </span>
              </div>

              <div className="form-group">
                <label htmlFor="kyc_gender">{t("gender")}</label>
                <select
                  className="form-control-custom"
                  name="kyc_gender"
                  // required
                  value={formik.values.kyc_gender}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <option value=''>{t("select_gender")}</option>
                  <option value='male'>{t("male")}</option>
                  <option value='female'>{t("female")}</option>
                </select>
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.kyc_gender && formik.touched.kyc_gender && formik.errors.kyc_gender}
                </span>
              </div>

              <div className="form-group">
                <div className="flex justify-between items-end mb-2">
                  <label htmlFor="kyc_mobile_number" className="mb-0">
                    {t("moblie_number")}
                  </label>
                  {isPhoneVerified ? (
                    <span className="text-green-500 font-medium text-sm flex items-center gap-1">
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M5 13l4 4L19 7"
                        ></path>
                      </svg>
                      Verified
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="text-primary-500 text-sm font-medium hover:underline focus:outline-none"
                      onClick={() => handleSendOtp(formik.values.kyc_mobile_number)}
                      disabled={isVerifyingOtp}
                    >
                      {isVerifyingOtp ? "Sending..." : "Verify Now"}
                    </button>
                  )}
                </div>
                <div className="flex items-center form-control-custom p-0 overflow-hidden">
                  <span className="px-3 text-zinc-500 bg-zinc-100 h-full flex items-center border-r border-zinc-200 text-sm">
                    +91
                  </span>
                  <input
                    id="kyc_mobile_number"
                    type="text"
                    placeholder={t("placeholder.phone")}
                    className="flex-1 px-3 outline-none bg-transparent h-full h-[44px]"
                    name="kyc_mobile_number"
                    value={formik.values.kyc_mobile_number}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                      formik.setFieldValue("kyc_mobile_number", val);
                    }}
                    onBlur={formik.handleBlur}
                  />
                </div>
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.kyc_mobile_number && formik.touched.kyc_mobile_number && t(formik.errors.kyc_mobile_number)}
                </span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="kyc_doc_type">{t("document_type")}</label>
              <select
                className="form-control-custom"
                name="kyc_doc_type"
                value={formik.values.kyc_doc_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                <option value=''>{t("select_document_type")}</option>
                <option value='aadhar'>{t("aadhar")}</option>
                <option value='pan'>{t("pan")}</option>
              </select>
              <span className="text-danger text-left d-blockerr-spn">
                {formik.errors.kyc_doc_type && formik.touched.kyc_doc_type && formik.errors.kyc_doc_type}
              </span>
            </div>

            <div className="form-group">
              <label htmlFor="kyc_doc_number">{t("document_number")}</label>
              <input
                id="kyc_doc_number"
                type="text"
                placeholder={
                  formik.values.kyc_doc_type === "aadhar"
                    ? "123456789012"
                    : formik.values.kyc_doc_type === "pan"
                      ? "ABCDE1234F"
                      : formik.values.kyc_doc_type === "passport"
                        ? "A1234567"
                        : t("placeholder.document_number")
                }
                className="form-control-custom"
                name="kyc_doc_number"
                value={formik.values.kyc_doc_number}
                onChange={(e) => {
                  let val = e.target.value;
                  if (formik.values.kyc_doc_type === "pan") {
                    val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                  } else if (formik.values.kyc_doc_type === "aadhar") {
                    val = val.replace(/[^0-9]/g, "");
                  } else if (formik.values.kyc_doc_type === "passport") {
                    val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                  }
                  formik.setFieldValue("kyc_doc_number", val);
                }}
                onBlur={formik.handleBlur}
                maxLength={
                  formik.values.kyc_doc_type === "aadhar"
                    ? 12
                    : formik.values.kyc_doc_type === "pan"
                      ? 10
                      : formik.values.kyc_doc_type === "passport"
                        ? 8
                        : 20
                }
                style={
                  formik.values.kyc_doc_type === "pan" || formik.values.kyc_doc_type === "passport"
                    ? { textTransform: "uppercase" }
                    : {}
                }
              />
              <span className="text-danger text-left d-blockerr-spn">
                {formik.errors.kyc_doc_number && formik.touched.kyc_doc_number && formik.errors.kyc_doc_number}
              </span>
            </div>

            <div className="form-group">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <label className="mb-0">
                  {formik.values.kyc_doc_type === "pan" ? t("Upload PAN") : t("upload_aadhar")}
                </label>
                {formik.values.kyc_doc_type?.toLowerCase() === "pan" && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-blue-50 text-blue-600 border border-blue-200 rounded">
                    Business Proof
                  </span>
                )}
                {(formik.values.kyc_doc_type?.toLowerCase() === "aadhar" || formik.values.kyc_doc_type?.toLowerCase() === "aadhaar") && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 rounded">
                    Address Proof
                  </span>
                )}
              </div>

              <div className="flex items-center gap-[15px] flex-wrap">
              <div
                onClick={() => kycDocFrontRef.current.click()}
                className="w-[122px] h-[122px] bg-primary-50 border border-dashed border-primary-400 flex items-center justify-center rounded-[8px] overflow-hidden relative cursor-pointer"
              >
                <img
                  src={
                    formik.values.kyc_doc_front
                      ? (typeof formik.values.kyc_doc_front === 'string'
                          ? formik.values.kyc_doc_front
                          : URL.createObjectURL(formik.values.kyc_doc_front))
                      : "/images/upload2.svg"
                  }
                  alt="upload"
                  className={
                    formik.values.kyc_doc_front
                      ? "w-full h-full object-cover"
                      : "w-[40px] h-[40px] object-contain opacity-60"
                  }
                />

                {!formik.values.kyc_doc_front && (
                  <p className="absolute bottom-1 text-xs text-primary-400">
                    {t("front")}
                  </p>
                )}
              </div>
                {/* Hidden input */}
                <input
                  ref={kycDocFrontRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  name="kyc_doc_front"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      formik.setFieldValue("kyc_doc_front", e.target.files[0]); // set file
                    }
                  }}
                />

              <div
                onClick={() => kycDocBackRef.current.click()}
                className="w-[122px] h-[122px] bg-primary-50 border border-dashed border-primary-400 flex items-center justify-center rounded-[8px] overflow-hidden relative cursor-pointer"
              >
                <img
                  src={
                    formik.values.kyc_doc_back
                      ? (typeof formik.values.kyc_doc_back === 'string'
                          ? formik.values.kyc_doc_back
                          : URL.createObjectURL(formik.values.kyc_doc_back))
                      : "/images/upload2.svg"
                  }
                  alt="upload"
                  className={
                    formik.values.kyc_doc_back
                      ? "w-full h-full object-cover"
                      : "w-[40px] h-[40px] object-contain opacity-60"
                  }
                />

                {!formik.values.kyc_doc_back && (
                  <p className="absolute bottom-1 text-xs text-primary-400">
                    {t("back")}
                  </p>
                )}
              </div>
                {/* Hidden input */}
                <input
                  ref={kycDocBackRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  name="kyc_doc_back"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      formik.setFieldValue("kyc_doc_back", e.target.files[0]); // set file
                    }
                  }}
                />

                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.kyc_doc_front && formik.touched.kyc_doc_front && formik.errors.kyc_doc_front}
                </span>
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.kyc_doc_back && formik.touched.kyc_doc_back && formik.errors.kyc_doc_back}
                </span>
              </div>
            </div>

            {/* <div className="form-group">
              <label>Social media link</label>
              {
                formik?.values?.kyc_social_media?.map((link, index) =>{
                  return  <div className="flex items-center gap-[15px] mt-2">
                    <input
                      id="document-number"
                      type="text"
                      placeholder="https://www.facebook.com/"
                      className="form-control-custom flex-1"
                      // required
                      name={`kyc_social_media${index}`}
                      value={formik.values.kyc_doc_number[index]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <button className="w-[44px] h-[44px] flex-[0,0,44px] rounded-full bg-primary-400 flex items-center justify-center">
                      <img src="/images/cross.png" alt="cross" />
                    </button>
                  </div>
                })
              }
              <button onClick={()=> {formik.setFieldValue('kyc_social_media', )}} 
                className="add-more-btn headings-h5 text-secondary-500 mb-3"
              >
                + Add More
              </button>
            </div> */}

            {/* <input
              id="document-number"
              type="text"
              placeholder="Enter title"
              className="form-control-custom"
              required
              name="kyc_doc_number"
              value={formik.values.kyc_doc_number}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            /> */}

            {/* <div className="form-group">
              <label className="block mb-2 font-medium">{t("social_media_link")}</label>

              {(formik.values.kyc_social_media || []).map((link, index) => {
                const fieldName = `kyc_social_media[${index}]`;
                const error =
                  formik.errors.kyc_social_media &&
                  Array.isArray(formik.errors.kyc_social_media) &&
                  formik.errors.kyc_social_media[index];

                return (
                  <div key={index} className="flex items-center gap-[15px] mt-2">
                    <input
                      id={`kyc_social_media_${index}`}
                      type="text"
                      placeholder={t("placeholder.social_media")}
                      className="form-control-custom flex-1"
                      name={fieldName}
                      value={formik.values.kyc_social_media[index] || ""}
                      onChange={(e) =>
                        formik.setFieldValue(fieldName, e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      aria-label={`Social link ${index + 1}`}
                    />

                    <button
                      type="button"
                      onClick={(e) => removeLink(e, index)}
                      className="w-[44px] h-[44px] flex-[0,0,44px] rounded-full bg-primary-400 flex items-center justify-center"
                      aria-label={`Remove link ${index + 1}`}
                    >
                      <img src="/images/cross.png" alt="Remove" />
                    </button>

                    {error && (
                      <div className="text-red-600 text-sm mt-1">{error}</div>
                    )}
                  </div>
                );
              })}

              <div className="mt-3">
                <button
                  type="button"
                  onClick={addLink}
                  className="add-more-btn headings-h5 text-secondary-500 mb-3"
                >
                  {t("add_more")}
                </button>
              </div>

             
              {typeof formik.errors.kyc_social_media === "string" && (
             
                <span className="text-danger text-left d-blockerr-spn">
                  {formik.errors.kyc_social_media && formik.touched.kyc_social_media && formik.errors.kyc_social_media}
                </span>
              )}
            </div> */}


            {/* Sign up Button */}
            <button
              type="submit"
              className="btn btn-pri w-full"
              disabled={submitted || isVerifyingOtp}
            >
              {submitted ? t("submitting", "Submitting...") : t("submit")}
            </button>
          </form>
        </div>

        {/* <div className="mt-8 text-center body-md text-zinc-700">
          {t("dont_have_account")}
          <a
            href="#"
            className="headings-h6 font-bold text-[var(--secondary-400)] pl-[5px]"
          >
            {t("signup")}
          </a>
        </div> */}
      </div>
    </section>
  );
}

export default KycDetail;
